import { createContext, useContext, useState } from "react";

type UserType = {
  name: string;
  email: string;
  uid: string;
} | null;

type AuthContextType = {
  currentUser: {
    name: string;
    email: string;
    uid: string;
  } | null;
  signUp: ({
    email,
    password,
    name,
  }: {
    email: string;
    password: string;
    name: string;
  }) => void;
  login: ({ email, password }: { email: string; password: string }) => void;
  logOut: () => void;
  status: string;
};

const AuthContext = createContext<AuthContextType>({
  currentUser: {
    name: "",
    email: "",
    uid: "",
  },
  signUp: () => {},
  login: () => {},
  logOut: () => {},
  status: "",
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider(props: any) {
  const [currentUser, setCurrentUser] = useState<UserType>(null);
  const [status, setStatus] = useState("");

  // Simulated user storage - in a real app, you'd want to use a proper backend
  const [users, setUsers] = useState<
    Array<{
      name: string;
      email: string;
      password: string;
      uid: string;
    }>
  >([]);

  function signUp({
    email,
    password,
    name,
  }: {
    email: string;
    password: string;
    name: string;
  }) {
    try {
      // Check if user already exists
      if (users.find((user) => user.email === email)) {
        throw new Error("User already exists");
      }

      // Create new user
      const newUser = {
        name,
        email,
        password, // In a real app, you'd want to hash this
        uid: Math.random().toString(36).substr(2, 9), // Simple UID generation
      };

      setUsers([...users, newUser]);
      setCurrentUser({
        name: newUser.name,
        email: newUser.email,
        uid: newUser.uid,
      });
      setStatus("success");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Sign up failed");
    }
  }

  function login({ email, password }: { email: string; password: string }) {
    try {
      const user = users.find(
        (user) => user.email === email && user.password === password
      );

      if (!user) {
        throw new Error("Invalid email or password");
      }

      setCurrentUser({
        name: user.name,
        email: user.email,
        uid: user.uid,
      });
      setStatus("success");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Login failed");
    }
  }

  function logOut() {
    window.localStorage.clear();
    setCurrentUser(null);
    setStatus("");
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        signUp,
        login,
        logOut,
        status,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
}
